import type { SortDescriptor } from '@react-types/shared';
import type { ExperimentItem } from '@/types';

import React from 'react';

import { createSortDescriptor } from '@/components/table';
import { getStatusOrder, resolveExperimentTypes } from '@/utils/experiments';

export function useExperimentsSorting(experiments: ExperimentItem[]) {
  const [sortDescriptor, setSortDescriptor] = React.useState<SortDescriptor | undefined>(undefined);

  const sortedExperiments = React.useMemo(() => {
    if (!sortDescriptor?.column) return experiments;

    const copy = [...experiments];
    const { column, direction } = sortDescriptor;

    const compareStrings = (a: string, b: string) =>
      a.localeCompare(b, undefined, { sensitivity: 'base' });

    copy.sort((a, b) => {
      let cmp = 0;

      switch (column) {
        case 'name': {
          cmp = compareStrings(a.name, b.name);
          break;
        }
        case 'status': {
          const aStatus = getStatusOrder(a.status);
          const bStatus = getStatusOrder(b.status);
          cmp = aStatus - bStatus;
          break;
        }
        case 'type': {
          const aTypes = resolveExperimentTypes(a).join(',');
          const bTypes = resolveExperimentTypes(b).join(',');
          cmp = compareStrings(aTypes, bTypes);
          break;
        }
        case 'domain': {
          cmp = compareStrings(a.domain.title, b.domain.title);
          break;
        }
        case 'organization': {
          cmp = compareStrings(a.domain.organization.name, b.domain.organization.name);
          break;
        }
        case 'created_at': {
          const aDate = new Date(a.created_at).getTime();
          const bDate = new Date(b.created_at).getTime();
          cmp = aDate - bDate;
          break;
        }
        case 'updated_at': {
          const aDate = new Date(a.updated_at).getTime();
          const bDate = new Date(b.updated_at).getTime();
          cmp = aDate - bDate;
          break;
        }
        default:
          cmp = 0;
      }

      return direction === 'descending' ? -cmp : cmp;
    });

    return copy;
  }, [experiments, sortDescriptor]);

  return {
    sortDescriptor,
    setSortDescriptor,
    sortedExperiments
  };
}
